import * as React from "react";
import { Field, Form, useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import Stack from "@mui/material/Stack";
import { DataTable } from "components/DataTable/DataTable";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import { CheckboxWithLabel } from "formik-mui";
import Box from "@mui/material/Box";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { SelectOption, SelectOptionGroup } from "@mui/base";
import { DataTableRowAction } from "components/DataTable/types";
import { useDialogState } from "hooks/useDialogState";
import { AddOptionDialog } from "../../AddOptionDialog";
import { CustomFieldDefinition } from "gen/clients";
import { SelectManyGroupOptionField } from "components/formikFields/SelectManyGroupOptionField/SelectManyGroupOptionField";
import { CustomFieldUtils } from "../../CustomFieldUtils";
import { PersonInfoInputField } from "components/formikFields/PersonInfoInputField/PersonInfoInputField";

interface Props {
  options: SelectOption<string>[];
  setOptions: (options: SelectOption<string>[]) => void;
  mappingOptions: SelectOptionGroup<string>[];
  onCancel: () => void;
  allowTextArea: boolean;
  otherFieldsForVisibility?: {
    fieldName: string;
    possibleValues?: SelectOption<string>[];
  }[];
}

export enum FieldName {
  NAME = "name",
  TYPE = "type",
  MAPPING = "mapping",
  DEFAULT_VALUE = "defaultValue",
  REQUIRED = "required",
  VISIBLE = "visible",
  VISIBLE_IF_FIELD_NAME = "visibleIfFieldName",
  VISIBLE_IF_FIELD_VALUE = "visibleIfFieldValue",
  VALIDATION_REGEX = "validationRegex",
  VALIDATION_MESSAGE = "validationMessage",
  DESCRIPTION = "description"
}

export interface FormValues {
  [FieldName.NAME]: string;
  [FieldName.TYPE]: CustomFieldDefinition.type;
  [FieldName.MAPPING]: string[];
  [FieldName.DEFAULT_VALUE]: string;
  [FieldName.REQUIRED]: boolean;
  [FieldName.VISIBLE]: "VISIBLE" | "HIDDEN" | "CONDITIONAL";
  [FieldName.VISIBLE_IF_FIELD_NAME]: string;
  [FieldName.VISIBLE_IF_FIELD_VALUE]: string;
  [FieldName.VALIDATION_REGEX]: string;
  [FieldName.VALIDATION_MESSAGE]: string;
  [FieldName.DESCRIPTION]: string;
}

const CustomFieldForm: React.FC<Props> = ({
  options,
  setOptions,
  onCancel,
  mappingOptions,
  allowTextArea,
  otherFieldsForVisibility
}) => {
  const { values, isSubmitting, submitCount } = useFormikContext<Record<string, unknown>>();

  const showOptionsError =
    submitCount > 0 && CustomFieldUtils.requiresOptions(values.type as CustomFieldDefinition.type) && !options.length;
  const [isAddOptionDialogOpen, openAddOptionDialog, closeAddOptionDialog] = useDialogState();

  const addOption = React.useCallback(
    (labelValue: SelectOption<string>) => {
      setOptions([...options, labelValue]);
      closeAddOptionDialog();
    },
    [options, setOptions, closeAddOptionDialog]
  );

  const onDeleteOptionClick = React.useCallback(
    (optionIndex: number) => {
      const optionsUpdate = [...options];
      optionsUpdate.splice(optionIndex, 1);
      setOptions(optionsUpdate);
    },
    [options, setOptions]
  );

  const onMoveOptionUpClick = React.useCallback(
    (optionIndex: number) => {
      const optionsUpdate = [...options];
      const option = optionsUpdate[optionIndex];
      optionsUpdate.splice(optionIndex, 1);
      optionsUpdate.splice(optionIndex - 1, 0, option);
      setOptions(optionsUpdate);
    },
    [options, setOptions]
  );

  const onMoveOptionDownClick = React.useCallback(
    (optionIndex: number) => {
      const optionsUpdate = [...options];
      const option = optionsUpdate[optionIndex];
      optionsUpdate.splice(optionIndex, 1);
      optionsUpdate.splice(optionIndex + 1, 0, option);
      setOptions(optionsUpdate);
    },
    [options, setOptions]
  );

  const optionRowActions = React.useCallback(
    (labelValue: SelectOption<string>, index: number) => {
      const actions: DataTableRowAction[] = [];
      if (index > 0) {
        actions.push({
          title: "Move Up",
          action: () => onMoveOptionUpClick(index)
        });
      }
      if (index < options.length - 1) {
        actions.push({
          title: "Move Down",
          action: () => onMoveOptionDownClick(index)
        });
      }
      actions.push({
        title: "Delete",
        action: () => onDeleteOptionClick(index),
        color: "error.light"
      });
      return actions;
    },
    [onMoveOptionUpClick, onMoveOptionDownClick, onDeleteOptionClick, options.length]
  );

  return (
    <>
      <Form noValidate={true}>
        <Grid container={true} spacing={2}>
          <Grid item={true} xs={12}>
            <TextInputField name={FieldName.NAME} label="Field Name" required={true} />
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            <SelectOneField
              name={FieldName.TYPE}
              label="Type"
              required={true}
              options={[
                {
                  label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.INPUT_TEXT),
                  value: CustomFieldDefinition.type.INPUT_TEXT
                },
                {
                  label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.LISTONLY),
                  value: CustomFieldDefinition.type.LISTONLY
                },
                {
                  label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.LIST),
                  value: CustomFieldDefinition.type.LIST
                },
                {
                  label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.SELECT_MANY_LIST),
                  value: CustomFieldDefinition.type.SELECT_MANY_LIST
                },
                {
                  label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.DATE),
                  value: CustomFieldDefinition.type.DATE
                },
                ...(allowTextArea
                  ? [
                      {
                        label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.TEXT_AREA),
                        value: CustomFieldDefinition.type.TEXT_AREA
                      }
                    ]
                  : []),
                {
                  label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.CHECKBOX),
                  value: CustomFieldDefinition.type.CHECKBOX
                },
                {
                  label: CustomFieldUtils.getLabelForType(CustomFieldDefinition.type.PERSON_INFO),
                  value: CustomFieldDefinition.type.PERSON_INFO
                }
              ]}
              helperText={
                values["type"] === CustomFieldDefinition.type.TEXT_AREA ? (
                  <Box color="warning.main">
                    Note: Text area can be used only with mappings that support multi-line input in XTRF, such as
                    "Internal Notes" and "Vendor Instructions."
                  </Box>
                ) : undefined
              }
            />
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            {["LISTONLY", "LIST", "SELECT_MANY_LIST"].includes(values.type as string) && (
              <Stack spacing={1}>
                <DataTable
                  data={options}
                  columns={[
                    {
                      id: "option",
                      title: "Options",
                      cell: option => (
                        <>
                          Label: {option.label}, Value: {option.value}
                        </>
                      )
                    }
                  ]}
                  rowKey={option => option.label}
                  rowActions={optionRowActions}
                  noData="No options defined"
                />
                {showOptionsError && <Box color="error.main">At least one option is required for this field type.</Box>}
                <Button onClick={openAddOptionDialog}>Add Option</Button>
              </Stack>
            )}
          </Grid>
          <Grid item={true} xs={12}>
            <SelectManyGroupOptionField
              name={FieldName.MAPPING}
              label="Mapping"
              options={mappingOptions}
              required={true}
              variant="standard"
            />
          </Grid>
          <Grid item={true} xs={12}>
            {values.type === CustomFieldDefinition.type.PERSON_INFO ? (
              <PersonInfoInputField name={FieldName.DEFAULT_VALUE} label="Default Value" />
            ) : (
              <TextInputField
                name={FieldName.DEFAULT_VALUE}
                label="Default Value"
                multiline={values.type === CustomFieldDefinition.type.TEXT_AREA}
                rows={values.type === CustomFieldDefinition.type.TEXT_AREA ? 5 : undefined}
                variant={values.type === CustomFieldDefinition.type.TEXT_AREA ? "outlined" : undefined}
                validationRegex={values.type === CustomFieldDefinition.type.CHECKBOX ? "^(?:true|false)$" : undefined}
                validationMessage="Must be true or false"
              />
            )}
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            <FormControl>
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                name={FieldName.REQUIRED}
                Label={{
                  label: "Required"
                }}
              />
            </FormControl>
          </Grid>
          <Grid item={true} xs={12}>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={4}>
                <SelectOneField
                  name={FieldName.VISIBLE}
                  label="Visibility"
                  required={true}
                  options={[
                    { label: "Visible", value: "VISIBLE" },
                    {
                      label: "Hidden",
                      value: "HIDDEN"
                    },
                    { label: "Conditional", value: "CONDITIONAL" }
                  ]}
                />
              </Grid>
              {values[FieldName.VISIBLE] === "CONDITIONAL" && (
                <>
                  <Grid item={true} xs={4}>
                    <SelectOneField
                      name={FieldName.VISIBLE_IF_FIELD_NAME}
                      label="Display if"
                      required={true}
                      options={
                        otherFieldsForVisibility?.map(field => ({
                          label: field.fieldName,
                          value: field.fieldName
                        })) || []
                      }
                    />
                  </Grid>
                  <Grid item={true} xs={4}>
                    <SelectOneField
                      name={FieldName.VISIBLE_IF_FIELD_VALUE}
                      label="Value"
                      required={true}
                      disabled={!values[FieldName.VISIBLE_IF_FIELD_NAME]}
                      options={
                        otherFieldsForVisibility
                          ?.find(f => f.fieldName === values[FieldName.VISIBLE_IF_FIELD_NAME])
                          ?.possibleValues?.map(val => ({
                            label: val.label,
                            value: val.value
                          })) || []
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
          <Grid item={true} xs={12}>
            <TextInputField name={FieldName.VALIDATION_REGEX} label="Validation Regex" />
          </Grid>
          <Grid item={true} xs={12}>
            <TextInputField name={FieldName.VALIDATION_MESSAGE} label="Validation Message" />
          </Grid>
          <Grid item={true} xs={12}>
            <TextInputField name={FieldName.DESCRIPTION} label="Description" />
          </Grid>
        </Grid>

        <Box sx={{ mt: 5 }}>
          <LoadingButton isLoading={isSubmitting}>Save</LoadingButton>
          <Button color="secondary" onClick={onCancel} disabled={isSubmitting} sx={{ ml: 1 }}>
            Cancel
          </Button>
        </Box>
      </Form>

      {isAddOptionDialogOpen && (
        <AddOptionDialog
          onClose={closeAddOptionDialog}
          onAdd={addOption}
          useLabelAsValue={values.type === "LIST"}
          valueSeparator={values.type === "SELECT_MANY_LIST" ? "|" : undefined}
        />
      )}
    </>
  );
};

export { CustomFieldForm };
